import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import MobileCTA from "@/components/site/MobileCTA";
import ScrollScanner from "@/components/ScrollScanner";
import Reveal from "@/components/site/Reveal";
import RelatedLinks from "@/components/site/RelatedLinks";
import RelatedGuides from "@/components/site/RelatedGuides";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { trackPhoneCallLead } from "@/lib/ads";
import { useSeo } from "@/lib/useSeo";
import { useJsonLd } from "@/lib/useJsonLd";
import { base44 } from "@/api/base44Client";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const faqsFor = (neighborhood, area) => [
  { q: `How fast can Meridian treat bed bugs in ${neighborhood}, ${area.name}?`, a: `We aim to schedule treatments in ${neighborhood} as quickly as possible. Availability depends on demand and property size — share your details through the booking form and we'll confirm the earliest appointment.` },
  { q: `Can bed bugs spread between units in ${neighborhood}?`, a: `Yes. In multi-unit buildings such as condos and apartments in ${neighborhood}, bed bugs can move between connected suites through shared walls and electrical pathways, so early action and — where relevant — coordination across units matters.` },
  { q: `Is your service discreet in ${neighborhood}?`, a: `Yes. Our team works with discretion in ${neighborhood} — unmarked service, respectful communication and professional application — so neighbours, guests and other residents aren't drawn into the situation.` },
];

export default function NeighborhoodPage() {
  const { slug, hood } = useParams();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, [slug, hood]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setArea(null);
    base44.entities.ServiceArea.filter({ slug })
      .then((rows) => { if (active) { setArea(rows?.[0] || null); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  const neighborhoods = area?.neighborhoods ? area.neighborhoods.split(",").map((n) => n.trim()).filter(Boolean) : [];
  const neighborhood = neighborhoods.find((n) => slugify(n) === hood) || null;
  const parentPath = area?.name === "Vancouver" ? "/bed-bug-treatment-vancouver" : `/bed-bug-treatment/${slug}`;
  const parentLabel = area?.name === "Vancouver" ? "Bed Bug Treatment in Vancouver" : (area ? `Bed Bug Treatment in ${area.name}` : "Bed Bug Treatment");

  useSeo({
    title: area && neighborhood ? `Bed Bug Control & Treatment in ${neighborhood}, ${area.name} | Meridian` : "Bed Bug Treatment | Meridian",
    description: area && neighborhood ? `Specialized bed bug treatment in ${neighborhood}, ${area.name} — signs of bed bugs, how treatment works, preparation and discreet follow-up from Meridian.` : "Specialized bed bug treatment across Vancouver and the Lower Mainland.",
    path: `/bed-bug-treatment/${slug}/${hood}`,
    type: "article",
  });

  useJsonLd("neighborhood-jsonld", area && neighborhood ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Bed Bug Treatment",
    "provider": { "@type": "PestControl", "name": "Meridian Bed Bug Treatment", "telephone": BRAND.phoneTel, "areaServed": [neighborhood, area.name] },
    "areaServed": [{ "@type": "City", "name": `${neighborhood}, ${area.name}` }],
    "url": `https://meridianbedbug.ca/bed-bug-treatment/${slug}/${hood}`
  } : {});

  const faqs = area && neighborhood ? faqsFor(neighborhood, area) : [];
  useJsonLd("neighborhood-faq-jsonld", faqs.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
  } : {});

  if (loading) {
    return (
      <div className="overflow-x-hidden pb-16 lg:pb-0">
        <Navbar />
        <div className="mx-auto max-w-3xl px-5 py-40">
          <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
          <div className="mt-6 space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-4 w-full animate-pulse rounded bg-muted" />)}</div>
        </div>
      </div>
    );
  }

  if (!area || !neighborhood) {
    return (
      <div className="overflow-x-hidden pb-16 lg:pb-0">
        <Navbar />
        <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-5 text-center">
          <h1 className="font-heading text-3xl font-bold text-primary">Location not found</h1>
          <Button asChild className="mt-6 h-11 bg-accent text-white hover:bg-accent/90"><Link to="/bed-bug-treatment-areas">View all service areas</Link></Button>
        </div>
        <Footer />
      </div>
    );
  }

  const title = `Bed Bug Control & Treatment in ${neighborhood}, ${area.name}`;
  return (
    <div className="overflow-x-hidden pb-16 lg:pb-0">
      <ScrollScanner />
      <Navbar />
      <main id="main-content">
        <section className="bg-background px-5 pt-32 pb-8 lg:px-8 lg:pt-40">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Bed Bug Treatment", to: parentPath }, { name: title }]} className="mb-4" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Service Area</span>
              <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-primary lg:text-4xl">{title}</h1>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">Specialist bed bug treatment for homes, condos, apartments, hotels and short-term rentals in {neighborhood}, {area.name}{area.region ? `, ${area.region}` : ""}. Discreet, professional and built around the bed bug lifecycle.</p>
            </Reveal>
          </div>
        </section>

        <section className="bg-background px-5 pt-12 pb-24 lg:px-8 lg:pt-16 lg:pb-32">
          <div className="mx-auto max-w-3xl space-y-8 text-base leading-relaxed text-muted-foreground">
            <Reveal>
              <div className="max-w-none space-y-6">
                <p>Bed bugs are a recurring problem in {neighborhood} because they travel easily between connected living environments and survive for months between feedings. A single reported case in a condo or apartment can spread through shared walls and electrical pathways before bites become routine, which is why a general pest spray is rarely enough. Meridian treats bed bugs as a specialty — with a disciplined process: Book, Prepare, Treat, Aftercare and Follow-up.</p>
                <h2 className="font-heading text-xl font-bold text-primary">Signs of bed bugs in {neighborhood}</h2>
                <p>Watch for itchy bites that appear in lines or clusters after sleeping, dark rust-coloured spots on sheets and mattress seams, shed translucent skins, and a faint sweet musty smell in heavily infested rooms. In multi-unit buildings across {neighborhood}, bed bugs can spread between suites, so early action matters — one unit's report doesn't always mean a single-unit problem.</p>
                <h2 className="font-heading text-xl font-bold text-primary">How treatment works in {neighborhood}</h2>
                <p>After booking, you receive clear preparation instructions — stripping and laundering bedding, reducing clutter near sleeping areas, and moving furniture away from walls where instructed. Treatment targets where bed bugs actually hide: mattress and box spring seams, bed frame joints, baseboard gaps, headboards and surrounding furniture. People and pets leave during treatment and return once the product has dried and the area is safe, following aftercare guidance.</p>
                <h2 className="font-heading text-xl font-bold text-primary">Prevention and follow-up</h2>
                <p>Prevention comes down to awareness after travel, careful inspection of second-hand furniture, and a quick response at the first sign of activity. Where the treatment program calls for it, a follow-up visit targets any newly hatched insects missed by the first pass. Every job in {neighborhood} is handled with discretion so neighbours, guests and other residents aren't drawn into the situation.</p>
              </div>
            </Reveal>
            <Reveal>
              <div className="rounded-lg border border-border bg-muted/30 p-6">
                <h3 className="font-heading text-lg font-bold text-primary">Ready to book a treatment in {neighborhood}?</h3>
                <p className="mt-2 text-sm text-muted-foreground">Call us or use the booking form on the home page to share a few details about your property.</p>
                <a href={`tel:${BRAND.phoneTel}`} onClick={trackPhoneCallLead} className="mt-4 inline-flex items-center gap-2 font-semibold text-accent">{BRAND.phoneDisplay}</a>
              </div>
            </Reveal>
            {neighborhoods.length > 1 && (
              <Reveal>
                <div>
                  <h3 className="font-heading text-base font-bold text-primary">Other neighbourhoods in {area.name}</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {neighborhoods.map((n) => (
                      <Link key={n} to={`/bed-bug-treatment/${slug}/${slugify(n)}`} className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{n}</Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
            <Reveal>
              <div>
                <h3 className="font-heading text-base font-bold text-primary">More service areas</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BRAND.areas.filter((a) => a !== area.name).map((a) => (
                    <Link key={a} to={a === "Vancouver" ? "/bed-bug-treatment-vancouver" : `/bed-bug-treatment/${slugify(a)}`} className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{a}</Link>
                  ))}
                </div>
                <Link to="/bed-bug-treatment-areas" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">View all service areas <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </Reveal>
            <RelatedGuides match={neighborhood} />
            <Reveal>
              <div>
                <h3 className="font-heading text-lg font-bold text-primary">{neighborhood} bed bug treatment — common questions</h3>
                <div className="mt-4 divide-y divide-border border-y border-border">
                  {faqs.map((f) => (
                    <div key={f.q} className="py-4">
                      <p className="font-heading text-sm font-semibold text-primary">{f.q}</p>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
        <RelatedLinks links={[
          { to: "/guide", label: "Bed Bug Guide", title: "Browse the full Bed Bug Guide", desc: "Practical articles on signs, preparation, treatment and prevention.", cta: "Read the guide" },
          { to: "/#booking", label: "Book", title: "Book a treatment", desc: "Send a few details about your property and we'll confirm availability.", cta: "Book treatment" },
          { to: parentPath, label: "Area", title: parentLabel, desc: "See the full treatment guide for this area.", cta: "Read more" },
        ]} />
        <div className="px-5 pb-24 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <Link to={parentPath} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-accent">
              <ArrowLeft className="h-4 w-4" /> Back to {area.name}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}